import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FlaskConical, Plus, Trash2 } from "lucide-react";
import {
  type ChannelDetail,
  type ChannelSettings,
  type DryRun,
  FILTER_KINDS,
  type FilterKind,
  type FilterRule,
  KINDS_WITH_VALUE,
  READY_RULES,
  type Replacement,
  TRANSLATE_LANGUAGES,
  byPublisher,
  closedSlots,
  newRuleId,
  ruleProblem,
  sourceLabel,
  useDryRun,
  willExpire,
} from "@/lib/aggregator";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SimpleSelect } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/cn";
import { ICON_BUTTON, TEXTAREA, errorText, reasonText } from "./helpers";
import { MediaStrip, RichTextView, Section } from "./parts";

interface TabProps {
  channel: ChannelDetail;
  draft: ChannelSettings;
  onChange: (next: ChannelSettings) => void;
  canEdit: boolean;
  onOpenConnections: () => void;
}

/** What a trial run showed — kept by the card, so that a look at another tab
 *  does not throw it away. */
export interface TrialProps {
  trial: DryRun | null;
  onTrial: (result: DryRun | null) => void;
}

const LANGUAGES = ["ru", "en", "uk", "de", "fr", "es", "tr", "ar"] as const;

function NumberField({
  id,
  label,
  hint,
  value,
  min,
  max,
  onChange,
  disabled,
}: {
  id: string;
  label: string;
  hint?: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}) {
  // What is being typed is kept as typed until the field is left. Held to its
  // bounds on every key, "85" in a field that starts at 40 came out as 100:
  // the "8" was raised to 40 before the "5" arrived.
  const [typed, setTyped] = useState<string | null>(null);
  const within = (n: number) => Math.max(min, Math.min(max, Math.round(n)));
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={typed ?? (Number.isFinite(value) ? value : "")}
        disabled={disabled}
        onChange={(e) => {
          setTyped(e.target.value);
          const next = Number(e.target.value);
          if (e.target.value !== "" && Number.isFinite(next) && within(next) === next) onChange(next);
        }}
        onBlur={() => {
          if (typed !== null && typed !== "" && Number.isFinite(Number(typed))) {
            onChange(within(Number(typed)));
          }
          setTyped(null);
        }}
      />
      {hint && <p className="text-xs leading-snug text-fg-muted">{hint}</p>}
    </div>
  );
}

/* ------------------------------------------------------------- processing */

/**
 * What happens to a post between the source and the channel — laid out in
 * the order it happens: what is not taken, what the model does, what is
 * cleaned out, what is added at the end, and whether a person looks first.
 */
export function ProcessingTab({
  channel,
  draft,
  onChange,
  canEdit,
  onOpenConnections,
  trial,
  onTrial,
}: TabProps & TrialProps) {
  const { t } = useTranslation();
  const set = <K extends keyof ChannelSettings>(key: K, value: ChannelSettings[K]) =>
    onChange({ ...draft, [key]: value });
  const sources = [
    { value: "all", label: t("aggregator.rules.every_source") },
    ...channel.source_list.map((s) => ({
      value: s.id,
      label: sourceLabel(s, t("aggregator.sources.closed_name")),
    })),
  ];
  const needsModel = draft.ai.mode !== "off" || draft.ai.ads !== "off" || draft.ai.topic.trim() !== "";

  return (
    <fieldset
      disabled={!canEdit}
      className="space-y-8 disabled:pointer-events-none disabled:opacity-80"
    >
      <DryRunPanel
        channel={channel}
        draft={draft}
        needsModel={needsModel}
        result={trial}
        setResult={onTrial}
      />

      <Section title={t("aggregator.filters.title")} hint={t("aggregator.filters.hint")}>
        <Rules rules={draft.filters} sources={sources} onChange={(rules) => set("filters", rules)} />
      </Section>

      <Section title={t("aggregator.duplicates.title")} hint={t("aggregator.duplicates.hint")}>
        <Switch
          checked={draft.duplicates.enabled}
          onCheckedChange={(enabled) => set("duplicates", { ...draft.duplicates, enabled })}
          label={t("aggregator.duplicates.enabled")}
        />
        {draft.duplicates.enabled && (
          <div className="grid gap-3 sm:grid-cols-2">
            <NumberField
              id="agg_dup_threshold"
              label={t("aggregator.duplicates.threshold")}
              hint={t("aggregator.duplicates.threshold_hint")}
              value={draft.duplicates.threshold}
              min={40}
              max={100}
              onChange={(threshold) => set("duplicates", { ...draft.duplicates, threshold })}
            />
            <div className="space-y-1.5">
              <Label>{t("aggregator.duplicates.action")}</Label>
              <SimpleSelect
                value={draft.duplicates.action}
                onValueChange={(action) =>
                  set("duplicates", { ...draft.duplicates, action: action as "skip" | "review" })
                }
                options={[
                  { value: "skip", label: t("aggregator.action.skip") },
                  { value: "review", label: t("aggregator.action.review") },
                ]}
              />
            </div>
          </div>
        )}
      </Section>

      <Section title={t("aggregator.ai.title")} hint={t("aggregator.ai.hint")}>
        {needsModel && !channel.has_ai_provider && (
          <Alert
            tone="warning"
            title={t("aggregator.ai.no_provider_title")}
            action={
              <Button size="sm" variant="outline" onClick={onOpenConnections}>
                {t("aggregator.ai.connect")}
              </Button>
            }
          >
            {t("aggregator.ai.no_provider_text")}
          </Alert>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>{t("aggregator.ai.mode")}</Label>
            <SimpleSelect
              value={draft.ai.mode}
              onValueChange={(mode) =>
                set("ai", { ...draft.ai, mode: mode as ChannelSettings["ai"]["mode"] })
              }
              options={(["off", "rewrite", "shorten", "translate", "custom"] as const).map((mode) => ({
                value: mode,
                label: t(`aggregator.ai.mode_${mode}`),
              }))}
            />
          </div>
          {draft.ai.mode !== "off" && (
            <div className="space-y-1.5">
              <Label>{t("aggregator.ai.language")}</Label>
              <SimpleSelect
                value={draft.ai.language}
                onValueChange={(language) => set("ai", { ...draft.ai, language })}
                options={LANGUAGES.map((code) => ({
                  value: code,
                  label: t(`aggregator.language.${code}`),
                }))}
              />
            </div>
          )}
        </div>
        {["rewrite", "shorten", "custom"].includes(draft.ai.mode) && (
          <div className="space-y-1.5">
            <Label>{t("aggregator.ai.style")}</Label>
            <SimpleSelect
              value={draft.ai.style}
              onValueChange={(style) =>
                set("ai", { ...draft.ai, style: style as ChannelSettings["ai"]["style"] })
              }
              options={(["neutral", "news", "casual", "expert"] as const).map((style) => ({
                value: style,
                label: t(`aggregator.ai.style_${style}`),
              }))}
            />
          </div>
        )}
        {draft.ai.mode === "custom" && (
          <div className="space-y-1.5">
            <Label htmlFor="agg_ai_prompt">{t("aggregator.ai.prompt")}</Label>
            <textarea
              id="agg_ai_prompt"
              rows={4}
              maxLength={4000}
              value={draft.ai.prompt}
              onChange={(e) => set("ai", { ...draft.ai, prompt: e.target.value })}
              placeholder={t("aggregator.ai.prompt_placeholder")}
              className={TEXTAREA}
            />
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>{t("aggregator.ai.ads")}</Label>
            <SimpleSelect
              value={draft.ai.ads}
              onValueChange={(ads) =>
                set("ai", { ...draft.ai, ads: ads as ChannelSettings["ai"]["ads"] })
              }
              options={(["off", "skip", "strip"] as const).map((ads) => ({
                value: ads,
                label: t(`aggregator.ai.ads_${ads}`),
              }))}
            />
          </div>
          {needsModel && (
            <div className="space-y-1.5">
              <Label>{t("aggregator.ai.on_failure")}</Label>
              <SimpleSelect
                value={draft.ai.on_failure}
                onValueChange={(on_failure) =>
                  set("ai", {
                    ...draft.ai,
                    on_failure: on_failure as ChannelSettings["ai"]["on_failure"],
                  })
                }
                options={(["review", "original", "skip"] as const).map((policy) => ({
                  value: policy,
                  label: t(`aggregator.ai.on_failure_${policy}`),
                }))}
              />
            </div>
          )}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="agg_ai_topic">{t("aggregator.ai.topic")}</Label>
          <Input
            id="agg_ai_topic"
            maxLength={500}
            value={draft.ai.topic}
            onChange={(e) => set("ai", { ...draft.ai, topic: e.target.value })}
            placeholder={t("aggregator.ai.topic_placeholder")}
          />
          <p className="text-xs text-fg-muted">{t("aggregator.ai.topic_hint")}</p>
        </div>
      </Section>

      <Section title={t("aggregator.translate.title")} hint={t("aggregator.translate.hint")}>
        <Switch
          checked={draft.translate.enabled}
          onCheckedChange={(enabled) => set("translate", { ...draft.translate, enabled })}
          label={t("aggregator.translate.enabled")}
        />
        {draft.translate.enabled && !channel.has_translator && (
          <Alert
            tone="warning"
            title={t("aggregator.translate.no_translator_title")}
            action={
              <Button size="sm" variant="outline" onClick={onOpenConnections}>
                {t("aggregator.translate.connect")}
              </Button>
            }
          >
            {t(byPublisher("aggregator.translate.no_translator_text"))}
          </Alert>
        )}
        {draft.translate.enabled && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t("aggregator.translate.language")}</Label>
              <SimpleSelect
                value={draft.translate.language}
                onValueChange={(language) => set("translate", { ...draft.translate, language })}
                options={TRANSLATE_LANGUAGES.map((code) => ({
                  value: code,
                  label: t(`aggregator.language.${code}`),
                }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t("aggregator.translate.on_failure")}</Label>
              <SimpleSelect
                value={draft.translate.on_failure}
                onValueChange={(on_failure) =>
                  set("translate", {
                    ...draft.translate,
                    on_failure: on_failure as ChannelSettings["translate"]["on_failure"],
                  })
                }
                options={(["review", "original", "skip"] as const).map((policy) => ({
                  value: policy,
                  label: t(`aggregator.translate.on_failure_${policy}`),
                }))}
              />
            </div>
          </div>
        )}
      </Section>

      <Section title={t("aggregator.cleanups.title")} hint={t("aggregator.cleanups.hint")}>
        <div className="grid gap-3 sm:grid-cols-2">
          {(["remove_footer", "remove_mentions", "remove_hashtags", "remove_emoji"] as const).map(
            (key) => (
              <Switch
                key={key}
                checked={draft.cleanups[key]}
                onCheckedChange={(value) => set("cleanups", { ...draft.cleanups, [key]: value })}
                label={t(`aggregator.cleanups.${key}`)}
                description={t(`aggregator.cleanups.${key}_hint`)}
              />
            ),
          )}
        </div>
        <div className="space-y-2">
          <Switch
            checked={draft.cleanups.first_paragraph}
            onCheckedChange={(value) =>
              set("cleanups", { ...draft.cleanups, first_paragraph: value })
            }
            label={t("aggregator.cleanups.first_paragraph")}
            description={t("aggregator.cleanups.first_paragraph_hint")}
          />
          {draft.cleanups.first_paragraph && sources.length > 2 && (
            <SimpleSelect
              className="h-8 sm:max-w-[24rem]"
              value={draft.cleanups.first_paragraph_source_id ?? "all"}
              onValueChange={(value) =>
                set("cleanups", {
                  ...draft.cleanups,
                  first_paragraph_source_id: value === "all" ? null : value,
                })
              }
              options={sources}
            />
          )}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>{t("aggregator.cleanups.links")}</Label>
            <SimpleSelect
              value={draft.cleanups.links}
              onValueChange={(links) =>
                set("cleanups", {
                  ...draft.cleanups,
                  links: links as ChannelSettings["cleanups"]["links"],
                })
              }
              options={(["keep", "remove", "replace"] as const).map((mode) => ({
                value: mode,
                label: t(`aggregator.cleanups.links_${mode}`),
              }))}
            />
          </div>
          {draft.cleanups.links === "replace" && (
            <div className="space-y-1.5">
              <Label htmlFor="agg_links_to">{t("aggregator.cleanups.links_to")}</Label>
              <Input
                id="agg_links_to"
                value={draft.cleanups.links_replace_with}
                onChange={(e) =>
                  set("cleanups", { ...draft.cleanups, links_replace_with: e.target.value })
                }
                placeholder="https://t.me/my_channel"
              />
            </div>
          )}
        </div>
        <Replacements
          items={draft.cleanups.replacements}
          sources={sources}
          onChange={(replacements) => set("cleanups", { ...draft.cleanups, replacements })}
        />
      </Section>

      <Section title={t("aggregator.ending.title")} hint={t("aggregator.ending.hint")}>
        <div className="space-y-1.5">
          <Label htmlFor="agg_signature">{t("aggregator.ending.signature")}</Label>
          <textarea
            id="agg_signature"
            rows={2}
            maxLength={600}
            value={draft.signature}
            onChange={(e) => set("signature", e.target.value)}
            placeholder={t("aggregator.ending.signature_placeholder")}
            className={TEXTAREA}
          />
          <p className="text-xs text-fg-muted">{t("aggregator.markup_hint")}</p>
        </div>
        <Switch
          checked={draft.credit_source}
          onCheckedChange={(value) => set("credit_source", value)}
          label={t("aggregator.ending.credit")}
          description={t("aggregator.ending.credit_hint")}
        />
      </Section>

      <Section title={t("aggregator.moderation.title")} hint={t("aggregator.moderation.hint")}>
        <SimpleSelect
          value={draft.moderation}
          onValueChange={(mode) => set("moderation", mode as ChannelSettings["moderation"])}
          options={[
            { value: "auto", label: t("aggregator.moderation.auto") },
            { value: "all", label: t("aggregator.moderation.all") },
          ]}
        />
      </Section>
    </fieldset>
  );
}

/* ------------------------------------------------------------------ rules */

function Rules({
  rules,
  sources,
  onChange,
}: {
  rules: FilterRule[];
  sources: { value: string; label: string }[];
  onChange: (rules: FilterRule[]) => void;
}) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<FilterKind>("stop_words");
  const patch = (id: string, change: Partial<FilterRule>) =>
    onChange(rules.map((rule) => (rule.id === id ? { ...rule, ...change } : rule)));

  const added = (kind: FilterKind): FilterRule => ({
    id: newRuleId(),
    kind,
    value: "",
    action: "skip",
    source_id: null,
    enabled: true,
  });

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-xs text-fg-muted">
        <span>{t("aggregator.filters.presets")}</span>
        {READY_RULES.map((ready) => (
          <Button
            key={ready}
            type="button"
            size="sm"
            variant="outline"
            disabled={rules.some((rule) => rule.kind === ready)}
            onClick={() => onChange([...rules, added(ready)])}
          >
            {t(`aggregator.filters.preset_${ready}`)}
          </Button>
        ))}
      </div>
      {rules.length === 0 && (
        <p className="rounded-lg border border-dashed border-border px-3 py-3 text-sm text-fg-muted">
          {t("aggregator.filters.empty")}
        </p>
      )}
      {rules.map((rule) => (
        <div
          key={rule.id}
          className={cn(
            "space-y-2 rounded-xl border border-border bg-bg-primary px-3 py-3",
            !rule.enabled && "opacity-60",
          )}
        >
          <div className="flex flex-wrap items-center gap-2">
            <Checkbox
              checked={rule.enabled}
              onCheckedChange={(value) => patch(rule.id, { enabled: value === true })}
              aria-label={t("aggregator.filters.enabled")}
            />
            <span className="min-w-0 flex-1 text-sm font-medium text-fg-primary">
              {t(`aggregator.filter.kind.${rule.kind}`)}
            </span>
            <SimpleSelect
              className="h-8 w-auto min-w-[9.5rem]"
              value={rule.action}
              onValueChange={(action) => patch(rule.id, { action: action as "skip" | "review" })}
              options={[
                { value: "skip", label: t("aggregator.action.skip") },
                { value: "review", label: t("aggregator.action.review") },
              ]}
            />
            <button
              type="button"
              className={ICON_BUTTON}
              aria-label={t("common.remove")}
              onClick={() => onChange(rules.filter((r) => r.id !== rule.id))}
            >
              <Trash2 size={15} strokeWidth={2} aria-hidden="true" />
            </button>
          </div>
          {KINDS_WITH_VALUE.has(rule.kind) && (
            <Input
              value={rule.value}
              onChange={(e) => patch(rule.id, { value: e.target.value })}
              placeholder={t(`aggregator.filter.placeholder.${rule.kind}`)}
              inputMode={rule.kind.endsWith("_length") ? "numeric" : undefined}
              aria-invalid={ruleProblem(rule) !== null}
            />
          )}
          {ruleProblem(rule) && (
            <p className="text-xs text-danger">{t(`aggregator.filters.${ruleProblem(rule)}`)}</p>
          )}
          <p className="text-xs text-fg-muted">{t(`aggregator.filter.hint.${rule.kind}`)}</p>
          {sources.length > 2 && (
            <SimpleSelect
              className="h-8"
              value={rule.source_id ?? "all"}
              onValueChange={(value) => patch(rule.id, { source_id: value === "all" ? null : value })}
              options={sources}
            />
          )}
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-2">
        <SimpleSelect
          className="min-w-[14rem] flex-1"
          value={kind}
          onValueChange={(value) => setKind(value as FilterKind)}
          options={FILTER_KINDS.map((value) => ({
            value,
            label: t(`aggregator.filter.kind.${value}`),
          }))}
        />
        <Button
          type="button"
          variant="outline"
          iconLeft={<Plus size={15} strokeWidth={2} aria-hidden="true" />}
          onClick={() => onChange([...rules, added(kind)])}
        >
          {t("aggregator.filters.add")}
        </Button>
      </div>
    </div>
  );
}

function Replacements({
  items,
  sources,
  onChange,
}: {
  items: Replacement[];
  sources: { value: string; label: string }[];
  onChange: (items: Replacement[]) => void;
}) {
  const { t } = useTranslation();
  const patch = (id: string, change: Partial<Replacement>) =>
    onChange(items.map((item) => (item.id === id ? { ...item, ...change } : item)));
  return (
    <div className="space-y-2">
      <Label>{t("aggregator.cleanups.replacements")}</Label>
      {items.map((item) => (
        <div key={item.id} className="space-y-2 rounded-xl border border-border bg-bg-primary px-3 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              className="min-w-[9rem] flex-1"
              value={item.find}
              onChange={(e) => patch(item.id, { find: e.target.value })}
              placeholder={t("aggregator.cleanups.find")}
              aria-label={t("aggregator.cleanups.find")}
            />
            <span className="text-fg-muted" aria-hidden="true">
              →
            </span>
            <Input
              className="min-w-[9rem] flex-1"
              value={item.replace}
              onChange={(e) => patch(item.id, { replace: e.target.value })}
              placeholder={t("aggregator.cleanups.replace_with")}
              aria-label={t("aggregator.cleanups.replace_with")}
            />
            <button
              type="button"
              className={ICON_BUTTON}
              aria-label={t("common.remove")}
              onClick={() => onChange(items.filter((r) => r.id !== item.id))}
            >
              <Trash2 size={15} strokeWidth={2} aria-hidden="true" />
            </button>
          </div>
          {!item.find.trim() && (
            <p className="text-xs text-danger">{t("aggregator.cleanups.needs_find")}</p>
          )}
          {sources.length > 2 && (
            <SimpleSelect
              className="h-8"
              value={item.source_id ?? "all"}
              onValueChange={(value) => patch(item.id, { source_id: value === "all" ? null : value })}
              options={sources}
            />
          )}
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        iconLeft={<Plus size={15} strokeWidth={2} aria-hidden="true" />}
        onClick={() =>
          onChange([
            ...items,
            { id: newRuleId(), find: "", replace: "", source_id: null },
          ])
        }
      >
        {t("aggregator.cleanups.add_replacement")}
      </Button>
    </div>
  );
}

/* ---------------------------------------------------------------- dry run */

/**
 * "What would happen": the settings in the form — saved or not — tried on the
 * sources' latest real posts. Nothing is published and nothing is kept.
 */
function DryRunPanel({
  channel,
  draft,
  needsModel,
  result,
  setResult,
}: {
  channel: ChannelDetail;
  draft: ChannelSettings;
  needsModel: boolean;
  result: DryRun | null;
  setResult: (result: DryRun | null) => void;
}) {
  const { t } = useTranslation();
  const run = useDryRun();
  const [error, setError] = useState<string | null>(null);
  const [useAi, setUseAi] = useState(false);

  const start = async () => {
    setError(null);
    try {
      setResult(await run.mutateAsync({ id: channel.id, settings: draft, use_ai: useAi }));
    } catch (err) {
      setResult(null);
      setError(errorText(err, t("aggregator.action_error")));
    }
  };

  return (
    <div className="space-y-3 rounded-xl border border-accent/40 bg-accent/5 px-4 py-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-[14rem] flex-1">
          <h3 className="text-sm font-semibold text-fg-primary">{t("aggregator.dry_run.title")}</h3>
          <p className="mt-0.5 text-xs leading-snug text-fg-muted">{t("aggregator.dry_run.hint")}</p>
        </div>
        <Button
          type="button"
          onClick={start}
          loading={run.isPending}
          disabled={run.isPending || channel.source_list.length === 0}
          iconLeft={<FlaskConical size={16} strokeWidth={2} aria-hidden="true" />}
        >
          {t("aggregator.dry_run.run")}
        </Button>
      </div>
      {needsModel && (
        <label className="flex cursor-pointer items-start gap-2 text-xs text-fg-muted">
          <Checkbox
            checked={useAi}
            disabled={!channel.has_ai_provider}
            onCheckedChange={(value) => setUseAi(value === true)}
            className="mt-0.5"
          />
          <span>{t("aggregator.dry_run.use_ai")}</span>
        </label>
      )}
      {channel.source_list.length === 0 && (
        <p className="text-xs text-fg-muted">{t("aggregator.dry_run.no_sources")}</p>
      )}
      {error && <Alert tone="danger">{error}</Alert>}
      {result && (
        // The run before this one stays in sight while the new one is on its
        // way — dimmed, so that it is not read as the answer to the new settings.
        <div className={cn("space-y-3", run.isPending && "opacity-50")} aria-busy={run.isPending}>
          {result.ai_skipped && <Alert tone="info">{t("aggregator.dry_run.ai_skipped")}</Alert>}
          {result.unread.length > 0 && (
            <Alert tone="warning">
              {t("aggregator.dry_run.unread", { names: result.unread.join(", ") })}
            </Alert>
          )}
          {result.items.length === 0 && (
            <p className="text-sm text-fg-muted">{t("aggregator.dry_run.empty")}</p>
          )}
          {result.items.map((item, n) => (
            <div
              // (A closed source has no address for each post: all of its
              // posts carry the source's own.)
              key={`${n}:${item.url}`}
              className="space-y-2 rounded-xl border border-border bg-bg-secondary px-4 py-3"
            >
              <div className="flex flex-wrap items-center gap-2 text-xs text-fg-muted">
                <Badge
                  tone={
                    item.status === "queued" ? "success" : item.status === "review" ? "warning" : "neutral"
                  }
                >
                  {t(`aggregator.dry_run.status_${item.status}`)}
                </Badge>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="truncate hover:underline"
                >
                  {item.source_title ?? `@${item.source_username}`}
                </a>
                {item.ai_provider && <Badge tone="accent">{item.ai_provider}</Badge>}
              </div>
              {item.reason && (
                <p className="text-xs text-fg-secondary">{reasonText(t, item.reason, item.detail)}</p>
              )}
              <MediaStrip media={item.media} missing={item.media_missing} t={t} />
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <div className="mb-1 text-xs font-medium text-fg-muted">
                    {t("aggregator.post.original")}
                  </div>
                  <RichTextView text={item.original} clamp />
                </div>
                <div>
                  <div className="mb-1 text-xs font-medium text-fg-muted">
                    {t("aggregator.post.result")}
                  </div>
                  {item.text ? (
                    <RichTextView text={item.text} clamp />
                  ) : (
                    <p className="text-sm text-fg-muted">{t("aggregator.dry_run.not_taken")}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- schedule */

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

/** When posts go out — on the company's clock. */
export function ScheduleTab({ channel, draft, onChange, canEdit }: TabProps) {
  const { t } = useTranslation();
  const plan = draft.schedule;
  const set = (change: Partial<ChannelSettings["schedule"]>) =>
    onChange({ ...draft, schedule: { ...plan, ...change } });
  const [slot, setSlot] = useState("");
  const hours = Array.from({ length: 24 }, (_, hour) => hour);
  const late = willExpire(channel.queued, plan);
  // A night's break longer than a post is kept means the night's posts never
  // come out in the morning — worth saying before it surprises anybody.
  const open = plan.hour_from === plan.hour_to % 24 ? 24 : (plan.hour_to - plan.hour_from + 24) % 24;
  const closedHours = 24 - open;

  const addSlot = () => {
    const match = /^(\d{1,2}):(\d{2})$/.exec(slot.trim());
    if (!match || Number(match[1]) > 23 || Number(match[2]) > 59) return;
    const value = `${match[1].padStart(2, "0")}:${match[2]}`;
    set({ slots: [...new Set([...plan.slots, value])].sort() });
    setSlot("");
  };

  return (
    <fieldset
      disabled={!canEdit}
      className="space-y-8 disabled:pointer-events-none disabled:opacity-80"
    >
      <Section
        title={t("aggregator.schedule.when_title")}
        hint={t("aggregator.schedule.when_hint", { timezone: channel.timezone })}
      >
        <div className="flex flex-wrap gap-1.5">
          {WEEKDAYS.map((day) => {
            const on = plan.days.includes(day);
            return (
              <button
                key={day}
                type="button"
                aria-pressed={on}
                onClick={() => {
                  const days = on ? plan.days.filter((d) => d !== day) : [...plan.days, day].sort();
                  if (days.length > 0) set({ days });
                }}
                className={cn(
                  "h-9 min-w-[2.75rem] rounded-lg border px-2 text-sm font-medium transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
                  on
                    ? "border-accent bg-accent/15 text-accent"
                    : "border-border text-fg-muted hover:text-fg-primary",
                )}
              >
                {t(`aggregator.weekday.${day}`)}
              </button>
            );
          })}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>{t("aggregator.schedule.hour_from")}</Label>
            <SimpleSelect
              value={String(plan.hour_from)}
              onValueChange={(value) => set({ hour_from: Number(value) })}
              options={hours.map((hour) => ({ value: String(hour), label: `${hour}:00` }))}
            />
          </div>
          <div className="space-y-1.5">
            <Label>{t("aggregator.schedule.hour_to")}</Label>
            <SimpleSelect
              value={String(plan.hour_to)}
              onValueChange={(value) => set({ hour_to: Number(value) })}
              options={[...hours.slice(1), 24].map((hour) => ({
                value: String(hour),
                label: `${hour}:00`,
              }))}
            />
          </div>
        </div>
        {closedHours > plan.stale_hours && (
          <Alert tone="info">
            {t("aggregator.schedule.night_note", { closed: closedHours, stale: plan.stale_hours })}
          </Alert>
        )}
      </Section>

      <Section title={t("aggregator.schedule.pace_title")} hint={t("aggregator.schedule.pace_hint")}>
        <div className="grid gap-3 sm:grid-cols-2">
          <NumberField
            id="agg_interval"
            label={t("aggregator.schedule.interval")}
            hint={t("aggregator.schedule.interval_hint")}
            value={plan.interval_minutes}
            min={0}
            max={1440}
            disabled={plan.slots.length > 0}
            onChange={(interval_minutes) => set({ interval_minutes })}
          />
          <NumberField
            id="agg_max_day"
            label={t("aggregator.schedule.max_per_day")}
            hint={t("aggregator.schedule.max_per_day_hint")}
            value={plan.max_per_day}
            min={0}
            max={500}
            onChange={(max_per_day) => set({ max_per_day })}
          />
          <NumberField
            id="agg_stale"
            label={t("aggregator.schedule.stale")}
            hint={t("aggregator.schedule.stale_hint")}
            value={plan.stale_hours}
            min={1}
            max={168}
            onChange={(stale_hours) => set({ stale_hours })}
          />
          <NumberField
            id="agg_delay"
            label={t("aggregator.schedule.delay")}
            hint={t("aggregator.schedule.delay_hint")}
            value={plan.delay_minutes}
            min={0}
            max={1440}
            onChange={(delay_minutes) => set({ delay_minutes })}
          />
        </div>
        {late > 0 && (
          <Alert tone="warning">
            {t("aggregator.schedule.will_expire", { count: late, hours: plan.stale_hours })}
          </Alert>
        )}
      </Section>

      <Section title={t("aggregator.schedule.slots_title")} hint={t("aggregator.schedule.slots_hint")}>
        <div className="flex flex-wrap items-center gap-2">
          {plan.slots.map((value) => (
            <span
              key={value}
              className="inline-flex items-center gap-1 rounded-full bg-bg-elevated py-1 pl-3 pr-1 font-mono text-sm"
            >
              {value}
              <button
                type="button"
                className="rounded-full p-0.5 text-fg-muted hover:text-fg-primary"
                aria-label={t("common.remove")}
                onClick={() => set({ slots: plan.slots.filter((s) => s !== value) })}
              >
                <Trash2 size={13} strokeWidth={2} aria-hidden="true" />
              </button>
            </span>
          ))}
          <Input
            className="h-9 w-28 font-mono"
            type="time"
            value={slot}
            onChange={(e) => setSlot(e.target.value)}
            aria-label={t("aggregator.schedule.slot_time")}
          />
          <Button type="button" size="sm" variant="outline" onClick={addSlot} disabled={!slot}>
            {t("aggregator.schedule.add_slot")}
          </Button>
        </div>
        {closedSlots(plan).length > 0 && (
          <Alert tone="danger">
            {t("aggregator.schedule.slots_closed", { slots: closedSlots(plan).join(", ") })}
          </Alert>
        )}
      </Section>

      <Section title={t("aggregator.publishing.title")}>
        <Switch
          checked={draft.publishing.silent}
          onCheckedChange={(silent) => onChange({ ...draft, publishing: { ...draft.publishing, silent } })}
          label={t("aggregator.publishing.silent")}
          description={t("aggregator.publishing.silent_hint")}
        />
        <Switch
          checked={draft.publishing.link_preview}
          onCheckedChange={(link_preview) =>
            onChange({ ...draft, publishing: { ...draft.publishing, link_preview } })
          }
          label={t("aggregator.publishing.link_preview")}
          description={t("aggregator.publishing.link_preview_hint")}
        />
      </Section>

      <Section title={t("aggregator.gates.title")} hint={t("aggregator.gates.hint")}>
        <NumberField
          id="agg_min_views"
          label={t("aggregator.gates.min_views")}
          hint={t("aggregator.gates.min_views_hint")}
          value={draft.min_views}
          min={0}
          max={100_000_000}
          onChange={(min_views) => onChange({ ...draft, min_views })}
        />
        {draft.min_views > 0 && plan.delay_minutes === 0 && (
          <Alert tone="warning">{t("aggregator.gates.needs_delay")}</Alert>
        )}
        <Switch
          checked={draft.check_still_there}
          onCheckedChange={(check_still_there) => onChange({ ...draft, check_still_there })}
          label={t("aggregator.gates.still_there")}
          description={t("aggregator.gates.still_there_hint")}
        />
      </Section>
    </fieldset>
  );
}
